// ============================================================================
// Contracts — Amendment chain UI (Phase 2b, 2026-06-10).
// Parent pill (this contract amends X) + children list (amendments of this
// contract). Parent is settable while Draft for amendment-type contracts;
// an Active amendment supersedes its parent commercially — the chain is the
// audit trail.
// ============================================================================

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { GitBranch } from 'lucide-react';
import {
  setParentContract,
  useContractChildren,
  useParentContractOptions,
} from './hooks/use-contract-data';
import {
  AMENDMENT_TYPES,
  CONTRACT_STATUS_CLASSES,
  CONTRACT_STATUS_LABELS,
  CONTRACT_TYPE_LABELS,
  type ContractRow,
} from './types';
import { errorMessage } from '../../shared/errors';

interface AmendmentLinksProps {
  contract: ContractRow;
  canEditDraft: boolean;
  onChanged: () => void;
}

export function AmendmentLinks({ contract, canEditDraft, onChanged }: AmendmentLinksProps) {
  const { data: children } = useContractChildren(contract.contract_id);
  const isAmendmentType = AMENDMENT_TYPES.includes(contract.contract_type);
  const parentOptions = useParentContractOptions(
    isAmendmentType && canEditDraft ? contract.account_id : null,
    contract.contract_id,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showParentPicker = isAmendmentType && canEditDraft;
  const hasContent =
    contract.parent_contract_id || children.length > 0 || showParentPicker;
  if (!hasContent) return null;

  async function handleParentChange(value: string) {
    setSaving(true);
    setError(null);
    try {
      await setParentContract(contract.contract_id, value || null);
      onChanged();
    } catch (err) {
      setError(errorMessage(err, 'Failed to set parent contract.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-md border border-tcs-border bg-tcs-bg p-4">
      <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-tcs-text">
        <GitBranch className="h-4 w-4 text-tcs-text-muted" /> Amendment chain
      </h3>

      {contract.parent_contract_id && (
        <p className="text-xs text-tcs-text-secondary">
          Amends{' '}
          <Link
            to={`/contracts/${contract.parent_contract_id}`}
            className="rounded-md bg-st-info-bg px-2 py-[2px] font-mono text-[11px] font-semibold text-st-info-fg-strong hover:underline"
          >
            {contract.parent_contract_id}
          </Link>
          {contract.status === 'Active' &&
            ' — this amendment supersedes the parent while Active.'}
        </p>
      )}

      {showParentPicker && (
        <label className="mt-2 block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
            Parent contract (optional —{' '}
            {CONTRACT_TYPE_LABELS[contract.contract_type]} usually amends an
            existing contract)
          </span>
          <select
            value={contract.parent_contract_id ?? ''}
            onChange={(e) => handleParentChange(e.target.value)}
            disabled={saving}
            className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
          >
            <option value="">— None —</option>
            {parentOptions.map((p) => (
              <option key={p.contract_id} value={p.contract_id}>
                {p.contract_number ?? p.contract_id} ·{' '}
                {CONTRACT_TYPE_LABELS[p.contract_type] ?? p.contract_type} ·{' '}
                {CONTRACT_STATUS_LABELS[p.status] ?? p.status}
              </option>
            ))}
          </select>
        </label>
      )}

      {error && <p className="mt-2 text-xs text-st-danger-fg">{error}</p>}

      {children.length > 0 && (
        <div className="mt-3">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
            Amendments of this contract ({children.length})
          </span>
          <ul className="space-y-1.5">
            {children.map((c) => (
              <li key={c.contract_id}>
                <Link
                  to={`/contracts/${c.contract_id}`}
                  className="flex items-center justify-between gap-2 rounded-md border border-tcs-border/60 bg-tcs-raised px-3 py-2 text-xs hover:bg-tcs-bg-muted"
                >
                  <span>
                    <span className="font-mono font-semibold text-tcs-text">
                      {c.contract_number ?? c.contract_id}
                    </span>
                    <span className="ml-2 text-tcs-text-muted">
                      {CONTRACT_TYPE_LABELS[c.contract_type] ?? c.contract_type}
                    </span>
                  </span>
                  <span
                    className={`inline-flex items-center rounded-md px-2 py-[1px] text-[10px] font-semibold uppercase tracking-wide ${
                      CONTRACT_STATUS_CLASSES[c.status] ?? 'bg-tcs-surface text-tcs-text-secondary'
                    }`}
                  >
                    {CONTRACT_STATUS_LABELS[c.status] ?? c.status}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
